import {
  getToolProofLabel,
  resolveToolDisplayTarget,
} from '@/lib/research-assistant/tool-presentation';

// ============================================================================
// Tool Args Formatting
// ============================================================================

const ENGINE_LABELS: Record<string, string> = {
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  google_aio: 'Google AI Overviews',
  chatgpt_searchapi: 'ChatGPT',
  gemini_searchapi: 'Gemini',
  perplexity_searchapi: 'Perplexity',
  bing_copilot: 'Bing Copilot',
  google_ai_mode: 'Google AI Mode',
  perplexity: 'Perplexity',
  claude: 'Claude',
  grok: 'Grok',
};

const STAGE_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  decision: 'Decision',
  trust: 'Trust',
};

/**
 * Format tool arguments for display in the UI.
 * Returns a compact, human-readable string summarizing the search parameters.
 */
export function formatToolArgs(toolName: string, args?: Record<string, unknown>): string | null {
  const target = resolveToolDisplayTarget(toolName, args);
  toolName = target.toolName;
  args = target.args;
  if (!args) return null;

  // Discovery queries describe internal capabilities, not the user's search.
  if (toolName === 'tools.search') return null;
  if (toolName === 'tools.describe') {
    return typeof args.toolName === 'string' ? getToolProofLabel(args.toolName) : null;
  }

  const parts: string[] = [];

  if (toolName === 'data.calculate') {
    const inputCount = Array.isArray(args.inputs) ? args.inputs.length : 0;
    const stepCount = Array.isArray(args.steps) ? args.steps.length : 0;
    if (inputCount > 0) parts.push(`${inputCount} data source${inputCount === 1 ? '' : 's'}`);
    if (stepCount > 0) parts.push(`${stepCount} step${stepCount === 1 ? '' : 's'}`);
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  if (toolName === 'data.calculate_batch') {
    const count = Array.isArray(args.calculations) ? args.calculations.length : 0;
    return count > 0 ? `${count} calculation${count === 1 ? '' : 's'}` : null;
  }

  // Query/search term (for search_user_pages, etc.)
  if (args.query && typeof args.query === 'string') {
    parts.push(`“${args.query}”`);
  }

  // Engine filter
  if (args.engine && typeof args.engine === 'string') {
    const engineLabel = ENGINE_LABELS[args.engine.toLowerCase()] || args.engine;
    parts.push(engineLabel);
  }

  // Stage filter (for get_brand_visibility)
  if (args.stage && typeof args.stage === 'string') {
    const stageLabel = STAGE_LABELS[args.stage.toLowerCase()] || args.stage;
    parts.push(stageLabel);
  }

  // Date range
  const dateValue = (value: unknown): string | undefined =>
    typeof value === 'string' && Number.isFinite(Date.parse(value)) ? value : undefined;
  const startDate = dateValue(args.startDate ?? args.start_date);
  const endDate = dateValue(args.endDate ?? args.end_date);
  if (startDate || endDate) {
    const formatDate = (d: string) => {
      return new Date(d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
    };

    if (startDate && endDate) {
      parts.push(`${formatDate(startDate)} – ${formatDate(endDate)}`);
    } else if (startDate) {
      parts.push(`from ${formatDate(startDate)}`);
    } else if (endDate) {
      parts.push(`until ${formatDate(endDate)}`);
    }
  }

  // Domain filter (for citations)
  if (args.domain && typeof args.domain === 'string') {
    parts.push(args.domain);
  }

  // Signal type (for brand perception)
  if (args.signal_type && typeof args.signal_type === 'string') {
    parts.push(args.signal_type.replaceAll('_', ' '));
  }

  // Growth Inbox issue category/severity (record_growth_issue)
  if (toolName === 'record_growth_issue') {
    if (args.category && typeof args.category === 'string') {
      parts.push(args.category.replaceAll('_', ' '));
    }
    if (args.severity && typeof args.severity === 'string') {
      parts.push(args.severity);
    }
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

// ============================================================================
// API Configuration
// Bypass Firebase App Hosting proxy for streaming to avoid 502 timeouts.
// Falls back to relative path for local development.
// ============================================================================

export const STREAMING_API_URL =
  process.env.NEXT_PUBLIC_STREAMING_API_URL || '/api/research-assistant';
